import { Loader2, Plus } from 'lucide-react';
import React, { useState, useCallback, useRef } from 'react';

import PaginationControls from './PaginationControls';

/**
 * ResponsiveGrid - A generic grid/list component with Progressive Loading (Infinite Scroll).
 * Now includes a manual "Load More" fallback for industrial production robustness.
 * 
 * @param {Object} props
 * @param {Array} props.items - Full list of items to filter/display
 * @param {Function} props.renderItem - Function to render each item: (item, index) => ReactNode
 * @param {string} [props.viewMode="grid"] - "grid" | "list"
 * @param {string} [props.gridClass] - Custom Tailwind classes for the grid container
 * @param {string} [props.listClass] - Custom Tailwind classes for the list container
 * @param {number} [props.defaultLimit=10] - Initial number of items to show
 * @param {number} [props.loadIncrement=12] - Number of items to add on each scroll trigger
 * @param {ReactNode} [props.emptyState] - Component to show when items array is empty
 */
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const ResponsiveGrid = ({ 
    items = EMPTY_ARRAY, 
    renderItem, 
    viewMode = 'grid',
    gridClass = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5 pt-0 px-0",
    listClass = "flex flex-col gap-3 pt-0 px-0",
    defaultLimit = 10,
    loadIncrement = 12,
    emptyState,
    
    // Pagination Integration
    showPagination = false,
    paginationProps = EMPTY_OBJECT
}) => {
    const [displayLimit, setDisplayLimit] = useState(() => defaultLimit);
    const observer = useRef();

    /**
     * Sentinel Callback - Intersection Observer logic
     * Triggers whenever the sentinel enters the viewport.
     */
    const lastElementRef = useCallback(node => {
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && displayLimit < items.length) {
                setDisplayLimit(prev => prev + loadIncrement);
            }
        }, { 
            threshold: 0.1,
            rootMargin: '200px' 
        });

        if (node) observer.current.observe(node);
    }, [displayLimit, items.length, loadIncrement]);

    const visibleItems = showPagination ? items : items.slice(0, displayLimit);
    const hasMore = showPagination ? false : displayLimit < items.length;

    if (items.length === 0 && emptyState) {
        return emptyState;
    }

    return (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto scrollbar-premium pb-10 pr-0 overscroll-contain">
                <div className="w-full max-w-[1920px] mx-auto px-4 md:px-6">
                    <div className={viewMode === 'grid' ? gridClass : listClass}>
                        {visibleItems.map((item, index) => renderItem(item, index))}
                    </div>

                    {/* Progressive Loading Sentinel (Only if NOT paginated) */}
                    {!showPagination && items.length > 0 && (
                        <div 
                            ref={lastElementRef}
                            className="py-12 flex flex-col items-center justify-center gap-4"
                        >
                            {hasMore ? (
                                <div className="flex flex-col items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-primary">
                                        <Loader2 className="animate-spin" size={20} />
                                    </div>
                                    <div className="flex flex-col items-center gap-3">
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">
                                            Synchronizing more assets...
                                        </span>
                                        
                                        {/* Manual Fallback Button - Safety net for production */}
                                        <button 
                                            onClick={() => setDisplayLimit(prev => prev + loadIncrement)}
                                            className="px-6 py-2 bg-white hover:bg-slate-50 text-slate-400 hover:text-primary rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-200 transition-all active:scale-95 shadow-sm flex items-center gap-2"
                                        >
                                            <Plus size={12} strokeWidth={3} />
                                            <span>Load More Manually</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 opacity-30">
                                    <div className="h-px w-12 bg-slate-400" />
                                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">
                                        End of List ({items.length})
                                    </span>
                                    <div className="h-px w-12 bg-slate-400" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Unified Pagination Footer - FIXED AT BOTTOM */}
            {showPagination && (
                <div className="hidden md:block shrink-0 bg-white/60 backdrop-blur-md border-t border-slate-100 pt-2 pb-0 z-20">
                    <PaginationControls {...paginationProps} />
                </div>
            )}
        </div>
    );
};

export default React.memo(ResponsiveGrid);
